/*
 *******************************************************************************
 *
 *  Filename:   @/pages/system/Users/UserEdit.js
 *
 *  Syntax:     NA
 *
 *  Synopsis:   Module for managing Users.
 *
 *  Author:     Norman J. Nolasco [ Archetact Software ]
 *
 *  Created:    Friday, August 18, 2023 - 6:07 AM (CST)
 *
 *  Notes:
 *
 *  Revisions:
 *              2023/08/18  NJN Initial Build
 *
 *  Copyright (c) 2023 - Archetact Software - All Rights Reserved.
 *  Unauthorized copying of this file via any medium is strictly prohibited.
 *  Proprietary and Confidential.
 *
 *******************************************************************************
 */

// External libraries
import React, { useEffect, useState, useRef } from "react";
import styled from "@emotion/styled";
import { spacing } from "@mui/system";
import * as Yup from "yup";
import { Formik } from "formik";
import { useQuery, useMutation } from "@apollo/client";

// Material UI components
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert as MuiAlert,
  Box,
  Button,
  Card as MuiCard,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField as MuiTextField,
  Typography,
} from "@mui/material";

// Local components
//import ColorSelect from "@/pages/utils/ColorSelect";

// Hooks
//import useAuth from "@/hooks/useAuth";
import { useSelector } from "react-redux";

// Utilities
//import { prepareAddresses, preparePhones } from "./formatting";

import { ExpandMore as ExpandMoreIcon } from "@mui/icons-material";
import Details from "./Details";

const Alert = styled(MuiAlert)(spacing);
const Card = styled(MuiCard)(spacing);
//const Spacer = styled.div(spacing);
const TextField = styled(MuiTextField)(spacing);

const DialogContainer = styled(DialogContent)`
  flex: 1;
  background: ${(props) => props.theme.palette.background.default};
  @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
    flex: none;
  }
`;

const UserEdit = ({
  handleClose,
  refetch,
  selectedRow,
  isEditMode,
  setIsEditMode,
  handleClickSnackbarAlert,
  isEditDialogOpen,
}) => {
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [initialValues, setInitialValues] = useState({});
  const [fieldTypes, setFieldTypes] = useState([]);
  const formikRef = useRef();

  const companyid = useSelector((state) => {
    return state.users.companyid;
  });
  const locationid = useSelector((state) => {
    return state.users.locationid;
  });
  const userstatusid = useSelector((state) => {
    return state.users.userstatusid;
  });

  useEffect(() => {
    setInitialValues({
      userid: selectedRow?.userid || 0,
      username: selectedRow?.username || "",
      firstname: selectedRow?.firstname || "",
      middlename: selectedRow?.middlename || "",
      lastname: selectedRow?.lastname || "",
      fullname: selectedRow?.fullname || "",
      userstatusid: selectedRow?.userstatusid || 0,
      avatarfileid: selectedRow?.avatarfileid || 0,
      companyid: selectedRow?.companyid || 0,
      division: selectedRow?.division || "",
      jobtitle: selectedRow?.jobtitle || "",
      phone: selectedRow?.phone || "",
      password: selectedRow?.password || "",
      createdate: selectedRow?.createdate || "",
      modifydate: selectedRow?.modifydate || "",
    });
    formikRef.current?.resetForm();
  }, [selectedRow]);

  const ValidationSchema = (isEditMode) =>
    Yup.object().shape({
      name: Yup.string().required("Required"),
    });

  const validationSchema = () => ValidationSchema(isEditMode);

  const updateList = async () => {};
  const updatingItem = false;
  const updateItemError = null;
  const createList = async () => {};
  const creatingItem = false;
  const createItemError = null;

  const handleSubmit = async (values, { setErrors, setStatus, setSubmitting, setFieldValue }) => {
    try {
      console.log("SAVING VALUES", values);
      console.log("EDIT MODE", isEditMode);

      setStatus({ sent: false });
      setIsFormSubmitted(false);

      const commonFields = {
        listgroupuuid: values.listgroupuuid,
        listuuid: values.listuuid,
        name: values.name,
        description: values.description,
        fieldcode: values.fieldcode,
        fieldtype: values.fieldtype,
      };

      if (isEditMode) {
        const response = await updateList({
          variables: {
            ...commonFields,
            pk: values.pk,
            sk: values.sk,
          },
        });
        console.log("updateItem response", response);
        handleClickSnackbarAlert("success", "List has been successfully updated.");
      } else {
        await createList({ variables: { ...commonFields } });
        handleClickSnackbarAlert("success", "List has been successfully created.");
        setIsEditMode(true);
      }

      setIsFormSubmitted(true);
      setStatus({ sent: true });
      setSubmitting(false);
      refetch();
    } catch (error) {
      console.error("ERROR ADD/UPDATE", error, error.networkError, error.graphQLErrors);
      handleClickSnackbarAlert("error", `Error: ${error.graphQLErrors[0]?.errorType}|${error.networkError}`);
      setIsFormSubmitted(false);
      setStatus({ sent: false });
      setErrors({ submit: error.errorType });
      setSubmitting(false);
    }
  };

  const alertConditions = [
    { condition: isFormSubmitted, severity: "success", message: "Your data has been submitted successfully!" },
    { condition: updatingItem, severity: "info", message: "Updating list group, please wait..." },
    { condition: creatingItem, severity: "info", message: "Creating list group, please wait..." },
    { condition: updateItemError, severity: "error", message: `Error: ${updateItemError?.graphQLErrors[0].errorType}` },
    { condition: createItemError, severity: "error", message: `Error: ${createItemError?.graphQLErrors[0].errorType}` },
  ];

  const renderAlert = () => {
    for (let i = 0; i < alertConditions.length; i++) {
      const alert = alertConditions[i];
      if (alert.condition) {
        return renderAlertMessage(alert.severity, alert.message);
      }
    }
  };

  const renderAlertMessage = (severity, message) => {
    return (
      <Alert severity={severity} sx={{ py: "0px" }}>
        {message}
      </Alert>
    );
  };

  const isFollowed = false;
  const clickFollow = () => {};
  const clickUpload = () => {};

  const [expanded, setExpanded] = React.useState(false);
  const handleSectionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <Formik
      innerRef={formikRef}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({ errors, handleBlur, handleChange, handleSubmit, touched, values, resetForm, submitForm }) => {
        return (
          <form onSubmit={handleSubmit}>
            <Dialog
              open={isEditDialogOpen}
              onClose={(e, reason) => {
                setIsFormSubmitted(false);
                handleClose(e, reason);
              }}
              fullWidth
              maxWidth="lg"
              className="editDialogStyle"
              classes={{ paper: "editPaperPropsStyle" }}
            >
              <DialogTitle>{isEditMode ? "Users > " + selectedRow.FULLNAME : "Add New User"}</DialogTitle>
              <DialogContainer style={{ padding: "10px 0px", margin: 0 }}>
                <Grid container spacing={0} mt={0}>
                  <Grid item xs={12} md={3} className="editLeftGridStyle">
                    <Details
                      values={values}
                      isFollowed={isFollowed}
                      clickFollow={clickFollow}
                      clickUpload={clickUpload}
                    />
                    <Card mb={6} variant="outlined">
                      <CardContent style={{ padding: "0px 20px" }}>
                        <Grid container alignItems="center">
                          <Grid item>
                            <p>password reset</p>
                            <p>update profile</p>
                            <p>update avatar</p>
                            <p>update plan</p>
                            <p>update security</p>
                            <p>update organization</p>
                            <p>update social</p>
                            <p>update addresses</p>
                            <p>update contacts</p>
                            <p>delete user</p>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={9} className="editRightGridStyle">
                    <Card mb={3} variant="outlined">
                      <CardContent>
                        <Grid container spacing={3}>
                          <Grid item xs={12} md={12}>
                            <Typography variant="h6">Summary</Typography>
                          </Grid>
                          <Grid item xs={12} md={9}>
                            <p>followers | messages | cases | patients</p>
                            <p>space usage | AI usage | tokens available</p>
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <p>flex plan</p>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                    <Card mb={3} variant="outlined">
                      <CardContent>
                        <Grid container spacing={0} alignItems="center">
                          <Grid item xs={11}>
                            <Typography variant="h6">Profile</Typography>
                            <Typography>basic info, statuses, primary phone, primary email</Typography>
                          </Grid>
                          <Grid item xs={1} container justify="flex-end" alignItems="flex-start">
                            <Button color="primary" variant="outlined" size="small" style={{ width: 80 }}>
                              Edit
                            </Button>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                    <Card mb={3} variant="outlined">
                      <CardContent>
                        <Grid container spacing={0} alignItems="center">
                          <Grid item xs={11}>
                            <Typography variant="h6">Flex Plan</Typography>
                            <Typography>plan</Typography>
                          </Grid>
                          <Grid item xs={1} container justify="flex-end" alignItems="flex-start">
                            <Button color="primary" variant="outlined" size="small" style={{ width: 80 }}>
                              Edit
                            </Button>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                    <Card mb={3} variant="outlined">
                      <CardContent>
                        <Grid container spacing={0} alignItems="center">
                          <Grid item xs={11}>
                            <Typography variant="h6">Security</Typography>
                            <Typography>roles, mfa</Typography>
                          </Grid>
                          <Grid item xs={1} container justify="flex-end" alignItems="flex-start">
                            <Button color="primary" variant="outlined" size="small" style={{ width: 80 }}>
                              Edit
                            </Button>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                    <Card mb={3} variant="outlined">
                      <CardContent>
                        <Grid container spacing={0} alignItems="center">
                          <Grid item xs={11}>
                            <Typography variant="h6">Organization</Typography>
                            <Typography>company/locations, division, job description</Typography>
                          </Grid>
                          <Grid item xs={1} container justify="flex-end" alignItems="flex-start">
                            <Button color="primary" variant="outlined" size="small" style={{ width: 80 }}>
                              Edit
                            </Button>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                    <Card mb={3} variant="outlined">
                      <CardContent>
                        <Grid container spacing={0} alignItems="center">
                          <Grid item xs={11}>
                            <Typography variant="h6">Social Media</Typography>
                            <Typography>facebook, instagram, twitter, thread</Typography>
                          </Grid>
                          <Grid item xs={1} container justify="flex-end" alignItems="flex-start">
                            <Button color="primary" variant="outlined" size="small" style={{ width: 80 }}>
                              Edit
                            </Button>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                    <Card mb={3} variant="outlined">
                      <CardContent>
                        <Grid container spacing={0} alignItems="center">
                          <Grid item xs={11}>
                            <Typography variant="h6">Address</Typography>
                            <Typography>mail, bill, office</Typography>
                          </Grid>
                          <Grid item xs={1} container justify="flex-end" alignItems="flex-start">
                            <Button color="primary" variant="outlined" size="small" style={{ width: 80 }}>
                              Edit
                            </Button>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                    <Card mb={3} variant="outlined">
                      <CardContent>
                        <Grid container spacing={0} alignItems="center">
                          <Grid item xs={11}>
                            <Typography variant="h6">Contacts</Typography>
                            <Typography>email, website, phone</Typography>
                          </Grid>
                          <Grid item xs={1} container justify="flex-end" alignItems="flex-start">
                            <Button color="primary" variant="outlined" size="small" style={{ width: 80 }}>
                              Edit
                            </Button>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
                {/*
                <Grid container spacing={6} mt={2}>
                  <Grid item xs={12} lg={12}>
                    <Card mb={3} variant="outlined">
                      <CardContent>
                        <Grid container spacing={3}>
                          <Grid item xs={12} lg={12}>
                            <TextField
                              name="username"
                              label="Username"
                              value={values.username}
                              error={Boolean(touched.username && errors.username)}
                              fullWidth
                              helperText={touched.username && errors.username}
                              onBlur={handleBlur}
                              onChange={handleChange}
                              type="text"
                              variant="outlined"
                              my={2}
                            />
                            <TextField
                              name="firstname"
                              label="First Name"
                              value={values.firstname}
                              error={Boolean(touched.firstname && errors.firstname)}
                              fullWidth
                              helperText={touched.firstname && errors.firstname}
                              onBlur={handleBlur}
                              onChange={handleChange}
                              type="text"
                              variant="outlined"
                              my={2}
                            />
                            <TextField
                              name="middlename"
                              label="Middle Name"
                              value={values.middlename}
                              error={Boolean(touched.middlename && errors.middlename)}
                              fullWidth
                              helperText={touched.middlename && errors.middlename}
                              onBlur={handleBlur}
                              onChange={handleChange}
                              type="text"
                              variant="outlined"
                              my={2}
                            />
                            <TextField
                              name="lastname"
                              label="Last Name"
                              value={values.lastname}
                              error={Boolean(touched.lastname && errors.lastname)}
                              fullWidth
                              helperText={touched.lastname && errors.lastname}
                              onBlur={handleBlur}
                              onChange={handleChange}
                              type="text"
                              variant="outlined"
                              my={2}
                            />
                          </Grid>
                          <Grid item xs={12} lg={12}>
                            <TextField
                              name="description"
                              label="Description"
                              value={values.description}
                              error={Boolean(touched.description && errors.description)}
                              fullWidth
                              helperText={touched.description && errors.description}
                              onBlur={handleBlur}
                              onChange={handleChange}
                              type="text"
                              variant="outlined"
                              multiline
                              rows={3}
                              my={2}
                            />
                          </Grid>
                          <Grid item xs={12} lg={12}>
                            <TextField
                              name="fieldcode"
                              label="Field Code"
                              value={values.fieldcode}
                              error={Boolean(touched.fieldcode && errors.fieldcode)}
                              fullWidth
                              helperText={touched.fieldcode && errors.fieldcode}
                              onBlur={handleBlur}
                              onChange={handleChange}
                              type="text"
                              variant="outlined"
                              my={2}
                            />
                          </Grid>
                          <Grid item xs={12} lg={12}>
                            <FormControl
                              sx={{
                                my: 2, // Increase this value to add more space at the top
                              }}
                              fullWidth
                            >
                              <InputLabel id="fieldtype">Field Type</InputLabel>
                              <Select
                                labelId="fieldtype"
                                id="fieldtype"
                                name="fieldtype"
                                value={values.fieldtype}
                                fullWidth
                                label="Field Type"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={Boolean(touched.fieldtype && errors.fieldtype)}
                                variant="outlined"
                                MenuProps={{
                                  PaperProps: {
                                    style: {
                                      maxHeight: "200px", // Adjust this value to control the maximum height of the dropdown
                                    },
                                  },
                                }}
                              >
                                {fieldTypes?.map((fieldType) => (
                                  <MenuItem key={fieldType.ListItemUUID} value={fieldType.Value}>
                                    {fieldType.Name}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} lg={6}>
                            <TextField
                              name="createdate"
                              label="Create Date"
                              value={values.createdate}
                              fullWidth
                              type="text"
                              variant="outlined"
                              disabled
                              my={2}
                            />
                          </Grid>
                          <Grid item xs={12} lg={6}>
                            <TextField
                              name="modifydate"
                              label="Last Modified"
                              value={values.modifydate}
                              fullWidth
                              type="text"
                              variant="outlined"
                              disabled
                              my={2}
                            />
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
                */}
              </DialogContainer>
              <DialogActions>
                <Box mx={2}>{renderAlert()}</Box>
                <Button
                  onClick={(e, reason) => {
                    setIsFormSubmitted(false);
                    handleClose(e, reason);
                  }}
                  color="primary"
                  variant="outlined"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    resetForm();
                  }}
                  color="primary"
                  variant="outlined"
                >
                  Reset
                </Button>
                <Button onClick={submitForm} color="primary" variant="contained">
                  Save
                </Button>
              </DialogActions>
            </Dialog>
          </form>
        );
      }}
    </Formik>
  );
};

export default UserEdit;
